/**
 * Quiet undo toast when the user Only-selects a workspace (or hides the
 * workspace that owned the foreground tab). Deep-link hideOtherWorkspaces
 * is skipped while restore is pending.
 */
import { useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { isDocumentDeepLinkRestorePending } from '@/lib/document-deep-link-pending'
import type { Workspace } from '@/providers/workspace-provider'
import {
  isTabVisibleForActiveWorkspaces,
  resolveTabWorkspaceId,
  type TabWorkspaceResolutionInput,
} from '@/components/layout/resizable-layout.handlers/shared'

const QUIET_TOAST_MS = 4000

export function useWorkspaceOnlyUndoToast(
  workspaces: Workspace[],
  activeWorkspaceIds: string[],
  setActiveWorkspaceIds: (ids: string[]) => void,
  openTabs: TabWorkspaceResolutionInput[],
  activeTabId: string | null,
  setActiveTabId: (id: string | null) => void,
): void {
  const prevIdsRef = useRef<string[]>(activeWorkspaceIds)
  const prevTabRef = useRef<string | null>(activeTabId)
  const prevTabVisibleRef = useRef(true)

  // Keep previous tab id while it is still in-scope (before Only parks it).
  useEffect(() => {
    if (!activeTabId) return
    const tab = openTabs.find((t) => t.id === activeTabId)
    if (!tab) return
    const visible = isTabVisibleForActiveWorkspaces(
      tab,
      workspaces,
      activeWorkspaceIds,
    )
    if (visible) {
      prevTabRef.current = activeTabId
      prevTabVisibleRef.current = true
    } else {
      prevTabVisibleRef.current = false
    }
  }, [activeTabId, openTabs, workspaces, activeWorkspaceIds])

  useEffect(() => {
    const prev = prevIdsRef.current
    const next = activeWorkspaceIds

    const same =
      prev.length === next.length && prev.every((id, i) => id === next[i])
    if (same) return

    // Cold deep link focuses a workspace via hideOtherWorkspaces - do not toast.
    if (isDocumentDeepLinkRestorePending()) {
      prevIdsRef.current = next
      return
    }

    const removed = prev.filter((id) => !next.includes(id))
    const isOnly =
      next.length === 1 &&
      (prev.length > 1 || (prev.length === 1 && prev[0] !== next[0]))

    const foregroundTab = openTabs.find((t) => t.id === prevTabRef.current)
    const foregroundWs = foregroundTab
      ? resolveTabWorkspaceId(foregroundTab, workspaces)
      : undefined
    const hidForegroundWorkspace =
      Boolean(foregroundWs) && removed.includes(foregroundWs!)

    if (!isOnly && !hidForegroundWorkspace) {
      prevIdsRef.current = next
      return
    }

    const destinationId = next.length === 1 ? next[0] : null
    const destinationName =
      (destinationId && workspaces.find((w) => w.id === destinationId)?.name) ||
      (next.length > 0
        ? workspaces.find((w) => w.id === next[0])?.name
        : null) ||
      'workspace'
    const previousIds = [...prev]
    const previousTabId = prevTabRef.current

    toast(`Switched to ${destinationName}`, {
      duration: QUIET_TOAST_MS,
      className: 'tab-groups-quiet-toast',
      action: {
        label: 'Undo',
        onClick: () => {
          setActiveWorkspaceIds(previousIds)
          if (previousTabId) {
            setActiveTabId(previousTabId)
          }
        },
      },
    })

    prevIdsRef.current = next
  }, [
    activeWorkspaceIds,
    workspaces,
    openTabs,
    setActiveWorkspaceIds,
    setActiveTabId,
  ])
}
